import React, { useEffect, useMemo, useRef, useState } from "react"

import { useSearchParams } from "react-router-dom"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import {
    IPoliciesListFiltersData,
    IPolicyColumnConfiguration,
} from "@common-shared/types/policies.types"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../../config/policies.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { usePoliciesList } from "../../../hooks/policiesList.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { policiesSetHistoryFilters } from "../../../store/slices/policies.slice"
import { SearchFiltersComponent } from "../filters/searchFilters.component"
import { BulkOverridesComponent } from "../overrides/bulkOverrides/bulkOverrides.component"
import { PoliciesListComponent } from "../policiesList/policiesList.component"
import { PoliciesListHeaderComponent } from "../policiesList/policiesListHeader.component"

export const SearchPoliciesComponent: React.FunctionComponent = () => {
    useScrollFix()

    useHasPermissions([OneVerifyPermission.viewHistoricalCoverageChecks])

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_SEARCH,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()

    const {
        // vars
        isOverridesEnabled,
        canManageOverrides,
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
    } = usePoliciesList()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { historyFilters } = useAppSelector(policiesSelector)

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const [filtersSubmitted, setFiltersSubmitted] = useState<boolean>(
        !!searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.ignoreListReload),
    )

    const [filtersFormKey, setFiltersFormKey] = useState<number>(
        new Date().getTime(),
    )

    // This component controls them and passes to list for display and header for modifications
    const [columnsConfiguration, setColumnsConfiguration] = useState<
        IPolicyColumnConfiguration[]
    >([])

    const listPoliciesSelectable = useMemo<boolean>(() => {
        return !!(canManageOverrides && isOverridesEnabled)
    }, [isOverridesEnabled, canManageOverrides])

    const showBulkOverrideButton = useMemo<boolean>(() => {
        return !!(isOverridesEnabled && canManageOverrides)
    }, [isOverridesEnabled, canManageOverrides])

    // Change columns configurations once practice role modalities change
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        setColumnsConfiguration(
            PoliciesListHelper.getColumnsConfiguration(
                PoliciesListType.HISTORY_SEARCH,
                selectedPracticeRole.availableModalities,
            ),
        )
    }, [selectedPracticeRole?.availableModalities])

    const onEditFilters = () => {
        // Deselect everything on filters change
        setSelectedPolicies([])

        setFiltersSubmitted(false)
    }

    const onResetFilters = async () => {
        dispatch(policiesSetHistoryFilters({}))
        setFiltersFormKey(new Date().getTime())
    }

    const onFiltersSubmitted = async (data: IPoliciesListFiltersData) => {
        dispatch(policiesSetHistoryFilters(data))
        setFiltersSubmitted(true)
    }

    const onSaveColumnsConfiguration = (
        columns: IPolicyColumnConfiguration[],
    ) => {
        setColumnsConfiguration(columns)

        PoliciesListHelper.saveColumnsConfigurations(
            PoliciesListType.HISTORY_SEARCH,
            columns,
            selectedPracticeRole?.availableModalities,
        )
    }

    const onChangeColumnOrder = (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => {
        const newColumns = PoliciesListHelper.changeSingleColumnOrdering(
            columnsConfiguration,
            columnLabelMoveSource,
            columnLabelMoveTarget,
            direction,
        )

        onSaveColumnsConfiguration(newColumns)
    }

    return selectedPracticeRole ? (
        <div className="pb-32 relative">
            <PageHelmetElement
                title={
                    POLICIES_CONFIG.headerTitleMapping[
                        PoliciesListType.HISTORY_SEARCH
                    ]
                }
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {filtersSubmitted &&
            Object.keys(historyFilters).filter(key => !!key).length ? (
                <>
                    <PoliciesListHeaderComponent
                        className="mb-[16px]"
                        listType={PoliciesListType.HISTORY_SEARCH}
                        setSelectedPolicies={setSelectedPolicies}
                        scrollableTableRef={scrollableTableRef}
                        onEditFilters={onEditFilters}
                        columnsConfiguration={columnsConfiguration}
                        onColumnsConfigurationChange={
                            onSaveColumnsConfiguration
                        }
                        ignoreFiltersPersistence
                        withExport
                    />

                    {columnsConfiguration.length ? (
                        <PoliciesListComponent
                            listType={PoliciesListType.HISTORY_SEARCH}
                            sortEnabled={true}
                            columns={columnsConfiguration}
                            scrollableElementRef={scrollableTableRef}
                            onChangeColumnOrder={onChangeColumnOrder}
                            policiesSelectable={listPoliciesSelectable}
                            selectedItems={selectedPolicies}
                            onSelectedItems={onPoliciesSelected}
                            onDeselectedItems={onPoliciesDeselected}
                            onDeselectedAllItems={onDeselectAllPolicies}
                        />
                    ) : null}

                    {showBulkOverrideButton && (
                        <BulkOverridesComponent
                            listType={PoliciesListType.HISTORY_SEARCH}
                            onCancelBulkOverride={onDeselectAllPolicies}
                            coverageChecksToOverride={selectedPolicies}
                            setCoverageChecksToOverride={setSelectedPolicies}
                        />
                    )}
                </>
            ) : (
                <SearchFiltersComponent
                    key={filtersFormKey}
                    className="px-[24px] w-[992px] md:w-full"
                    onFiltersSubmitted={onFiltersSubmitted}
                    onResetFilters={onResetFilters}
                />
            )}
        </div>
    ) : (
        <SpinnerElement containerClassName="mt-[32px] text-center" />
    )
}
